import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    where, 
    onSnapshot, 
    doc, 
    updateDoc, 
    serverTimestamp 
} from "firebase/firestore";

// CORRECCIÓN: Rutas relativas recalibradas de forma precisa a la profundidad de la carpeta RefacturacionPolizas
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth'; 

import { useTableState } from '../hooks/useTableState';
import { useDataHydration } from '../hooks/useDataHydration';

/**
 * useRefacturacionPolizasTable - Hook de negocio unificado para el Tablero de Refacturaciones de Pólizas.
 */
export const useRefacturacionPolizasTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS CRUDA ---
    const [polizas, setPolizas] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showRefacModal, setShowRefacModal] = useState(false); 
    const [showDebtModal, setShowDebtModal] = useState(false);
    const [showFichaModal, setShowFichaModal] = useState(false); 
    const [activePoliza, setActivePoliza] = useState(null);

    // ALERTA DE ÚLTIMA REFACTURACIÓN
    const [showLastRefacAlert, setShowLastRefacAlert] = useState(false);
    const [lastRefacPolizaInfo, setLastRefacPolizaInfo] = useState(null);

    // REFS DE CONCURRENCIA
    const activeItemPathRef = useRef(null);

    // --- 1. HOOK DE HIDRATACIÓN EN BACKGROUND (useDataHydration) ---
    const { clientsCache } = useDataHydration(polizas);

    // --- 2. LISTENER DE FIRESTORE EN TIEMPO REAL CON REGLAS DE NEGOCIO ---
    useEffect(() => {
        const today = new Date(); today.setUTCHours(0, 0, 0, 0); 
        const thirtyFromStr = new Date(new Date().setDate(today.getDate() + 30)).toISOString().split('T')[0];
        
        const q = query(
            collectionGroup(db, 'polizas'), 
            where('estadoEmision', 'in', ['NO INICIADA', 'VIGENTE', 'VENCIDA'])
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const rawData = snapshot.docs.map(docSnap => ({ 
                id: docSnap.id, 
                path: docSnap.ref.path, 
                clienteId: docSnap.ref.path.split('/')[3], 
                ...docSnap.data() 
            }));

            const processed = [];
            const subriesgosCaucionPermitidos = [
                'ALQUILER PARTICULAR', 'ALQUILER COMERCIAL', 'ADMINISTRADOR DE SOCIEDADES (IGJ)', 
                'AMBIENTAL', 'EMPRESAS DE VIAJES Y TURISMO', 'EJERCICIO DE LA ACTIVIDAD Y/O PROFESIÓN'
            ];

            rawData.forEach(p => {
                // Filtros de exclusión estrictos por negocio
                if (p.estadoEmision === 'ANULADA' || p.estadoGestionRenovacion === 'ANULADA') return;
                if (['RENOVADA', 'NO RENUEVA'].includes(p.estadoGestionRenovacion)) return;

                // --- MEJORA 1: FILTRO DE FIN DE CICLO ---
                // Si la próxima fecha de refacturación ya iguala o supera la fecha de vencimiento final,
                // significa que la póliza ya cubrió todos sus cobros y no debe listarse aquí.
                if (p.fechaRefacturacion && p.fechaVencimiento && p.fechaRefacturacion >= p.fechaVencimiento) {
                    return; 
                }

                // Validación de subriesgos Caución
                if ((p.riesgo || '').toUpperCase() === 'CAUCION') {
                    const normSub = (p.subriesgo || '').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
                    if (!subriesgosCaucionPermitidos.includes(normSub)) return;
                }

                // Ventana de refacturaciones (hasta 30 días en el futuro)
                if (p.fechaRefacturacion && p.fechaRefacturacion <= thirtyFromStr) {
                    processed.push({ ...p, vencimientoDate: p.fechaRefacturacion });
                }
            });

            setPolizas(processed);
            setLoading(false);
        }, (error) => console.error("Error cargando polizas para refacturacion:", error));

        return () => unsubscribe();
    }, []);

    // --- 3. MAPEO E HISTORIAL DE ENTIDADES (WATERFALL FALLBACK) ---
    const itemsTable = useMemo(() => {
        return polizas.map(p => {
            const c = clientsCache[p.clienteId] || {};
            let displayNombre = `${(p.clienteNombre || '').trim()} ${(p.clienteApellido || '').trim()}`.trim();
            if (!displayNombre) {
                if (c.persona === 'JURIDICA') displayNombre = (c.nombre || '').trim().toUpperCase();
                else displayNombre = `${c.nombre || ''} ${c.apellido || ''}`.trim().toUpperCase();
            }
            if (!displayNombre) displayNombre = 'S/D';
            const displayDoc = p.clienteCuit || p.clienteDni || c.cuit || c.dni || '-';

            // --- MEJORA 2: DETERMINAR SI ES LA ÚLTIMA REFACTURACIÓN ---
            let esUltimaRefac = false;
            if (p.fechaRefacturacion && p.fechaVencimiento) {
                const fRefac = new Date(p.fechaRefacturacion);
                const fVenc = new Date(p.fechaVencimiento);
                const diffTime = fVenc.getTime() - fRefac.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                // Si faltan 31 días o menos para el vencimiento anual/semestral, es el último cobro intermedio
                if (diffDays > 0 && diffDays <= 31) {
                    esUltimaRefac = true;
                }
            }

            return { 
                ...p, 
                displayNombre, 
                displayDoc, 
                esUltimaRefac,
                canal: p.canal || '',
                canalNombre: p.canalNombre || '',
                subcanalNombre: p.subcanalNombre || '' // ◄ LECTURA EXCLUSIVA Y DIRECTA DE subcanalNombre
            };
        });
    }, [polizas, clientsCache]);

    // --- 4. PREVENCIÓN DE PARPADEO (FUC) ---
    // Mantenemos el spinner si las pólizas están cargadas pero aún no resolvieron sus clientes de caché
    const needsHydration = useMemo(() => {
        if (polizas.length === 0) return false;
        return polizas.some(p => !clientsCache[p.clienteId]);
    }, [polizas, clientsCache]);

    // --- 5. HOOK DE CONTROL SÍNCRONO DE TABLAS DE GESTIÓN (useTableState) ---
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'vencimientoDate', direction: 'ascending' },
        initialColumns: {
            vencimientoDate: true, nPoliza: true, compania: true,
            displayNombre: true, displayDoc: true, riesgo: true, formaPago: true, canal: true
        },
        recordsPerPage: 100
    });

    // Desbloqueo manual/automático
    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al desbloquear póliza para refacturación:", e); }
    }, []);

    // --- 6. INTERCEPTOR DE CONTROLADORES DE ACCIÓN ---
    const handleAction = useCallback(async (poliza, type) => {
        setActivePoliza(poliza);
        if (type === 'ficha') { setShowFichaModal(true); return; }
        if (type === 'view') { setShowDetailModal(true); return; }
        
        if (type === 'refac') {
            activeItemPathRef.current = poliza.path; 
            setShowRefacModal(true);
            
            await updateDoc(doc(db, poliza.path), { 
                bloqueo: { 
                    userId: user?.uid || 'sys', 
                    userName: profile?.userName || 'Usuario', 
                    timestamp: serverTimestamp() 
                } 
            }).catch(() => { activeItemPathRef.current = null; });
        }
    }, [user, profile]);

    const handleShowDebt = useCallback((poliza) => {
        setActivePoliza(poliza);
        setShowDebtModal(true);
    }, []);

    const handleRefacSuccess = useCallback(() => {
        if (!activePoliza) return;
        const wasLast = activePoliza.esUltimaRefac;
        const polizaBackup = { ...activePoliza };

        unlockPoliza(activePoliza.path); 
        setShowRefacModal(false); 
        
        if (wasLast) {
            // Almacenamos los datos para mostrarlos en la advertencia persistente de derivación a Renovaciones
            setLastRefacPolizaInfo(polizaBackup);
            setShowLastRefacAlert(true);
        } else {
            setActivePoliza(null); 
        }
    }, [activePoliza, unlockPoliza]);

    return {
        loading,
        needsHydration,
        clientsCache,
        activePoliza,
        showDetailModal,
        showRefacModal,
        showDebtModal,
        showFichaModal,
        showLastRefacAlert,
        lastRefacPolizaInfo,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowDetailModal,
        setShowRefacModal,
        setShowDebtModal,
        setShowFichaModal,
        setShowLastRefacAlert,
        setLastRefacPolizaInfo,
        setActivePoliza,
        handleAction,
        handleShowDebt,
        handleRefacSuccess,
        unlockPoliza
    };
};